import { useEffect, useCallback, useMemo } from 'react';
import {
  Stack,
  Group,
  Title,
  Text,
  Button,
  Loader,
  Alert,
  Box,
  Badge,
  Anchor,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from '@tanstack/react-router';
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import { MediaStateTypeId } from '@lib2/model/media/MediaStateTypeObj';
import { ListlistTypeId } from '@lib2/model/listlist/ListlistType';
import { createPfileIds } from '@lib2/model/pfile/PfileUtil';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { PfileImageUploader } from '@/shared/components/PfileImageUploader';
import { CONTENT_IMAGE_SPECS } from '@/lib/constants/pfileImage';
import { APP_CONTENT_QUERY_KEYS } from '@/lib/constants/appContent';
import { useAppContentListlist } from '../useAppContentListlist';
import { useAppContentPositions } from '../useAppContentPositions';
import { useUpdateListlist } from '../useAppContentMutations';
import { AppContentUtils, type ListlistFormData } from '../AppContentUtils';
import { listlistFormSchema } from '../schemas/ListlistForm.schema';
import { ListlistSettingsSection } from './ListlistSettingsSection';
import { OrderedSeasonsEditor } from './OrderedSeasonsEditor';
import { usePlaylists } from '@/features/playlists/usePlaylists';

export function SeriesDetailPage() {
  const { id: displayId } = useParams({ strict: false });
  const navigate = useNavigate();
  const { selectedCId } = useAuth();

  // Parse base-converted ID
  let llId = 0;
  let idError = false;
  try {
    llId = AppContentUtils.getNumericId(displayId!);
    if (!llId || llId <= 0) idError = true;
  } catch {
    idError = true;
  }

  const { data: listlist, isLoading, isError, error } = useAppContentListlist(llId);
  const { data: positions } = useAppContentPositions();
  const { data: playlistData } = usePlaylists();
  const updateMutation = useUpdateListlist(llId);

  const llr = listlist?.llr ?? null;
  const isActive = llr ? llr.mediaStateTypeId === MediaStateTypeId.Active : false;

  // Form for settings
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<ListlistFormData>({
    resolver: zodResolver(listlistFormSchema),
    defaultValues: {
      title: '',
      descShort: '',
      mediaStateTypeId: MediaStateTypeId.Active,
    },
  });

  // Sync form when data loads
  useEffect(() => {
    if (llr) {
      reset(AppContentUtils.transformToFormData(llr));
    }
  }, [llr, reset]);

  const handleSave = handleSubmit((data: ListlistFormData) => {
    const req = AppContentUtils.buildUpdateReq(data);
    updateMutation.mutate(req, {
      onSuccess: () => {
        notifications.show({
          title: 'Saved',
          message: 'Series settings updated.',
          color: 'green',
        });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  });

  const handleDiscard = useCallback(() => {
    if (llr) {
      reset(AppContentUtils.transformToFormData(llr));
    }
  }, [llr, reset]);

  // Get ordered season (list/playlist) IDs from positions
  const orderedChildren = positions
    ? AppContentUtils.getOrderedChildren(
        llId,
        [...(positions.rootNodes ?? []), ...(positions.categoryNodes ?? []), ...(positions.seriesNodes ?? [])],
      )
    : null;

  const orderedListIds = orderedChildren?.listIds ?? [];

  // Build playlist lookup from playlists data
  const playlistLookup = useMemo(() => {
    const map = new Map<number, { listId: number; title: string }>();
    if (!playlistData) return map;
    const allLists = [...(playlistData.customLists ?? []), ...(playlistData.tagLists ?? [])];
    for (const p of allLists) {
      map.set(p.listId, { listId: p.listId, title: p.title });
    }
    return map;
  }, [playlistData]);

  // ── Error states ──

  if (idError) {
    return (
      <Stack>
        <Anchor
          component="button"
          size="sm"
          c="dimmed"
          onClick={() => navigate({ to: '/apps/content', search: { tab: 'series' } })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <IconArrowLeft size={14} />
          Back to Content
        </Anchor>
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          Invalid series ID.
        </Alert>
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading series...</Text>
      </Stack>
    );
  }

  if (isError || !llr) {
    return (
      <Stack>
        <Anchor
          component="button"
          size="sm"
          c="dimmed"
          onClick={() => navigate({ to: '/apps/content', search: { tab: 'series' } })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <IconArrowLeft size={14} />
          Back to Content
        </Anchor>
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error?.message ?? 'Failed to load series'}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {/* Back link */}
      <Box>
        <Anchor
          component="button"
          size="sm"
          c="dimmed"
          onClick={() => navigate({ to: '/apps/content', search: { tab: 'series' } })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <IconArrowLeft size={14} />
          Back to Content
        </Anchor>
      </Box>

      {/* Header */}
      <Group justify="space-between" align="flex-start">
        <Box>
          <Title order={2}>{llr.title}</Title>
          <Group gap="xs" mt={4}>
            <Text size="sm" c="dimmed" ff="monospace">
              {AppContentUtils.getDisplayId(llr.llId)}
            </Text>
            <Badge variant="light" color={isActive ? 'green' : 'gray'} size="sm">
              {isActive ? 'Active' : 'Inactive'}
            </Badge>
          </Group>
        </Box>
      </Group>

      {/* Settings Form */}
      <form onSubmit={handleSave}>
        <Box
          pos="sticky"
          top="calc(var(--app-shell-header-height, 60px) + var(--mantine-spacing-md))"
          bg="white"
          py="sm"
          style={{ zIndex: 10, borderBottom: '1px solid var(--mantine-color-gray-2)' }}
        >
          <Group justify="space-between">
            <Text fw={600} size="lg">
              Settings
            </Text>
            <Group gap="sm">
              <Button variant="default" onClick={handleDiscard} disabled={!isDirty}>
                Discard
              </Button>
              <Button
                type="submit"
                disabled={!isDirty}
                loading={updateMutation.isPending}
              >
                Save Changes
              </Button>
            </Group>
          </Group>
        </Box>

        <Box mt="md">
          <ListlistSettingsSection control={control} />
        </Box>
      </form>

      {/* Poster Image */}
      <Box mt="xl">
        {CONTENT_IMAGE_SPECS.map((spec) => (
          <PfileImageUploader
            key={spec.pfileTypeId}
            pfiles={listlist?.files ?? []}
            spec={spec}
            pfileIds={createPfileIds({
              llId,
              pfileTypeId: spec.pfileTypeId,
            })}
            invalidateQueryKeys={
              selectedCId
                ? [
                    APP_CONTENT_QUERY_KEYS.detail(selectedCId, llId),
                    APP_CONTENT_QUERY_KEYS.listlists(selectedCId),
                  ]
                : []
            }
          />
        ))}
      </Box>

      {/* Ordered Seasons (Playlists) Editor */}
      <Box mt="xl">
        <OrderedSeasonsEditor
          parentLlId={llId}
          orderedListIds={orderedListIds}
          playlistLookup={playlistLookup}
        />
      </Box>
    </Stack>
  );
}
